import { useEffect, useRef } from "react";
import QuestionText from "./QuestionText";
import { useTheme } from "../context/ThemeContext";
import { partAnswerKey } from "../utils/assessmentKeys";
import {
  formatOptionList,
  scaleLabel,
  scaleOptions,
  shortScaleName,
  trackLabel,
} from "../utils/participantAssessmentType";

const DEFAULT_IMPORTANCE = [
  "Not Important",
  "Important",
  "Very Important",
  "Critical",
];

const DEFAULT_AGREEMENT = [
  "Not at all",
  "To a small extent",
  "To a moderate extent",
  "To a great extent",
  "Completely",
  "Unable to assess",
];

function StepIntro({ step }) {
  if (!step) return null;
  const sub = step.subtitle;
  return (
    <div className="mt-3">
      {Array.isArray(sub) && sub.length > 0 ? (
        <ol className="list-decimal pl-6">
          {sub.map((item, index) => (
            <li
              key={index}
              className="py-2 text-sm font-medium text-[#4A4A4A]"
            >
              <span dangerouslySetInnerHTML={{ __html: item }} />
            </li>
          ))}
        </ol>
      ) : typeof sub === "string" ? (
        /<\/?[a-z][\s\S]*>/i.test(sub) ? (
          <div
            className="text-sm font-medium text-[#4A4A4A]"
            dangerouslySetInnerHTML={{ __html: sub }}
          />
        ) : (
          <p className="text-sm font-medium text-[#4A4A4A]">{sub}</p>
        )
      ) : (
        <p className="text-sm text-gray-500 italic">No subtitle available</p>
      )}
    </div>
  );
}

function resolveQuestionType(question) {
  const explicit = (question.type || "").toLowerCase();
  if (
    explicit === "dropdown" ||
    explicit === "text" ||
    explicit === "mcq" ||
    explicit === "multi" ||
    explicit === "rating"
  ) {
    return explicit;
  }
  const part = question.parts?.[0];
  const opts = part?.options || [];
  if (part?.type === "rating") return "rating";
  if (part?.type === "importance" && opts.length && !isStandardImportanceOptions(opts)) {
    return "dropdown";
  }
  return "mcq";
}

function isStandardImportanceOptions(options) {
  return (
    options.length === DEFAULT_IMPORTANCE.length &&
    options.every((opt, i) => opt === DEFAULT_IMPORTANCE[i])
  );
}

function parseMultiValues(value) {
  return String(value || "")
    .split("|")
    .map((item) => item.trim())
    .filter(Boolean);
}

function renderMultiQuestionBlock(question, answers, setAnswerField, readOnly) {
  const part = question.parts?.[0] || { type: "choice", options: [] };
  const key = partAnswerKey(question, part);
  const selected = parseMultiValues(answers[key] ?? part.answer ?? "");
  const options = part.options || [];
  return (
    <div className="w-full">
      <p className="text-[11px] font-bold tracking-widest uppercase text-slate-500 mb-2.5">
        Select all that apply
      </p>
      <div className="flex flex-wrap gap-2" role="group" aria-label="Multiple choice">
        {options.map((option) => {
          const isSelected = selected.includes(option);
          return (
            <button
              key={option}
              type="button"
              disabled={readOnly}
              aria-pressed={isSelected}
              onClick={() => {
                const next = isSelected
                  ? selected.filter((item) => item !== option)
                  : [...selected, option];
                setAnswerField?.(question, { ...part, type: part.type || "choice" }, next.join(" | "));
              }}
              className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm border ${
                isSelected
                  ? "bg-[#004F73] text-white border-[#004F73]"
                  : "bg-white text-slate-700 border-slate-200"
              } ${readOnly ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function renderDropdownQuestionBlock(question, answers, setAnswerField, readOnly) {
  const part = question.parts?.[0] || { type: "importance", options: [] };
  const dk = partAnswerKey(question, part);
  const options = part.options || [];
  const value = answers[dk] ?? part.answer ?? "";
  // v1 admin preview: enabled select, options listed directly (no blank placeholder)
  const preview = readOnly;
  return (
    <div className="w-full">
      <select
        value={preview && !value ? options[0] || "" : value}
        onChange={(e) => setAnswerField?.(question, part, e.target.value)}
        disabled={false}
        className="w-full p-2 border border-gray-300 rounded-md bg-white"
      >
        {!preview && (
          <option value="" disabled>
            Select the option
          </option>
        )}
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );
}

function renderTextQuestionBlock(question, answers, setAnswerField, readOnly) {
  const part = question.parts?.[0] || { type: "text" };
  const ak = partAnswerKey(question, part);
  return (
    <div className="w-full">
      <textarea
        value={
          Object.prototype.hasOwnProperty.call(answers, ak)
            ? answers[ak]
            : part.answer || ""
        }
        onChange={(e) =>
          setAnswerField?.(question, { ...part, type: part.type || "text" }, e.target.value)
        }
        disabled={readOnly}
        className="w-full p-2 border border-gray-300 rounded-md disabled:bg-gray-50"
        rows={4}
        placeholder={
          readOnly ? "Enter your response" : "Enter your response (at least 2 characters)..."
        }
      />
    </div>
  );
}

function McqChipTrack({ question, ptype, part, answers, setAnswerField, readOnly, assessmentType }) {
  const { theme } = useTheme();
  const dark = theme === "dark";
  const typeOpts = assessmentType ? scaleOptions(assessmentType, ptype) : [];
  const labels =
    ptype === "importance"
      ? part.options?.length ? part.options : (typeOpts.length ? typeOpts : DEFAULT_IMPORTANCE)
      : part.options?.length ? part.options : (typeOpts.length ? typeOpts : DEFAULT_AGREEMENT);
  const radioKey = partAnswerKey(question, part);
  const selected = answers[radioKey] ?? part.answer ?? "";
  const isImportance = ptype === "importance";

  const chipBase = {
    display: "inline-flex",
    alignItems: "center",
    padding: "7px 15px",
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 500,
    border: "1px solid",
    cursor: readOnly ? "not-allowed" : "pointer",
    whiteSpace: "nowrap",
    lineHeight: 1.3,
    transition: "background 0.13s ease, border-color 0.13s ease, color 0.13s ease, box-shadow 0.13s ease",
    opacity: readOnly ? 0.55 : 1,
  };
  const chipIdle = dark
    ? {
        background: "rgba(15,23,42,0.85)",
        borderColor: "rgba(148,163,184,0.35)",
        color: "rgba(241,248,251,0.92)",
      }
    : {
        background: "#ffffff",
        borderColor: "#e2e8f0",
        color: "#334155",
      };
  const chipActive = dark
    ? {
        background: "#0b6a8f",
        borderColor: "#67e8f9",
        color: "#ffffff",
        boxShadow: "0 0 0 3px rgba(56,189,248,0.22), 0 3px 18px rgba(0,79,115,0.55)",
      }
    : {
        background: "#004f73",
        borderColor: "#004f73",
        color: "#ffffff",
        boxShadow: "0 0 0 3px rgba(0,79,115,0.18), 0 3px 14px rgba(0,79,115,0.38)",
      };

  return (
    <div>
      {/* section label row */}
      <div className="flex items-center gap-2 mb-2.5">
        <span
          className="text-[11px] font-bold tracking-widest uppercase"
          style={{ color: dark ? "rgba(148,163,184,0.95)" : "#64748b" }}
        >
          {isImportance ? shortScaleName(assessmentType, "importance") : shortScaleName(assessmentType, "agreement")}
        </span>
        {selected && (
          <>
            <span
              className="text-[11px]"
              style={{ color: dark ? "rgba(148,163,184,0.55)" : "#94a3b8" }}
            >
              ·
            </span>
            <span
              className="text-[11px] font-semibold"
              style={{ color: dark ? "#67e8f9" : "#004f73" }}
            >
              {selected}
            </span>
          </>
        )}
      </div>

      {/* chip track */}
      <div
        role="group"
        aria-label={scaleLabel(assessmentType, isImportance ? "importance" : "agreement")}
        className="flex flex-wrap gap-2"
      >
        {labels.map((label) => {
          const isSelected = selected === label;
          return (
            <button
              key={label}
              type="button"
              role="radio"
              aria-checked={isSelected}
              disabled={readOnly}
              onClick={() => setAnswerField?.(question, part, label)}
              className={`mcq-chip ${isSelected ? "mcq-chip-selected" : "mcq-chip-default"}`}
              style={{
                ...chipBase,
                ...(isSelected ? chipActive : chipIdle),
              }}
            >
              {isSelected && (
                <svg
                  width="10" height="10"
                  viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth={3}
                  style={{ marginRight: 5, flexShrink: 0 }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function renderStarRatingBlock(question, part, answers, setAnswerField, readOnly) {
  const ak = partAnswerKey(question, part);
  const raw = Object.prototype.hasOwnProperty.call(answers, ak)
    ? answers[ak]
    : part.answer || "";
  const selected = Number(raw) || 0;
  const max = Number(part.max_stars) || 5;
  return (
    <div className="w-full">
      <p className="text-[11px] font-bold tracking-widest uppercase text-slate-500 mb-2.5">
        Rate 1–{max}
      </p>
      <div className="flex items-center gap-1.5" role="radiogroup" aria-label="Star rating">
        {Array.from({ length: max }, (_, i) => i + 1).map((n) => {
          const on = selected >= n;
          return (
            <button
              key={n}
              type="button"
              disabled={readOnly}
              aria-checked={selected === n}
              role="radio"
              onClick={() =>
                setAnswerField?.(question, { ...part, type: "rating" }, String(n))
              }
              className={`text-2xl leading-none transition-colors ${
                on ? "text-amber-400" : "text-slate-300"
              } ${readOnly ? "cursor-default" : "hover:text-amber-300"}`}
              title={`${n} star${n === 1 ? "" : "s"}`}
            >
              ★
            </button>
          );
        })}
        {selected > 0 && (
          <span className="ml-2 text-sm font-medium text-slate-600">{selected}/{max}</span>
        )}
      </div>
    </div>
  );
}

function renderPartBlock(
  question,
  qtype,
  part,
  answers,
  setAnswerField,
  readOnly,
  assessmentType
) {
  const ptype = (part.type || "").toLowerCase();

  if (qtype === "rating" || ptype === "rating") {
    return renderStarRatingBlock(question, part, answers, setAnswerField, readOnly);
  }

  if (qtype === "mcq" && (ptype === "importance" || ptype === "agreement")) {
    return (
      <McqChipTrack
        question={question}
        ptype={ptype}
        part={part}
        answers={answers}
        setAnswerField={setAnswerField}
        readOnly={readOnly}
        assessmentType={assessmentType}
      />
    );
  }

  if (
    qtype === "text" &&
    (ptype === "text" || ptype === "textarea" || ptype === "comment" || !part.type)
  ) {
    const ak = partAnswerKey(question, part);
    return (
      <div className="w-full">
        <textarea
          value={
            Object.prototype.hasOwnProperty.call(answers, ak)
              ? answers[ak]
              : part.answer || ""
          }
          onChange={(e) =>
            setAnswerField?.(question, { ...part, type: part.type || "text" }, e.target.value)
          }
          disabled={readOnly}
          className="w-full p-2 border border-gray-300 rounded-md disabled:bg-gray-50"
          rows={4}
          placeholder="Enter your response (at least 2 characters)..."
        />
      </div>
    );
  }

  if (qtype === "dropdown") {
    const dk = partAnswerKey(question, part);
    return (
      <div className="w-full">
        <select
          value={answers[dk] ?? part.answer ?? ""}
          onChange={(e) => setAnswerField?.(question, part, e.target.value)}
          disabled={readOnly}
          className="w-full p-2 border border-gray-300 rounded-md disabled:bg-gray-50"
        >
          <option value="" hidden disabled>
            Select the option
          </option>
          {(part.options || []).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return null;
}

export function V1StyleQuestionBlocks({
  stepData,
  questions: questionsOverride,
  answers,
  setAnswerField,
  readOnly = false,
  onEditQuestion,
  onDeleteQuestion,
  onMoveQuestion,
  onAskQuestion,
  onAskTerm,
  onFocusedQuestionChange,
  focusMode = "intersection",
  activeQuestionId = null,
  sectionLabel,
  assessmentType,
}) {
  const questionList = questionsOverride ?? stepData?.questions ?? [];
  const questionRefs = useRef({});
  const listRootRef = useRef(null);
  const lastFocusedRef = useRef("");

  useEffect(() => {
    lastFocusedRef.current = "";
  }, [questionList]);

  useEffect(() => {
    if (!onFocusedQuestionChange || !questionList.length) return undefined;

    const scrollTargets = new Set();

    const bindScrollTargets = () => {
      scrollTargets.clear();
      scrollTargets.add(window);
      let node = listRootRef.current?.parentElement;
      while (node && node !== document.body) {
        const { overflowY } = window.getComputedStyle(node);
        if (
          (overflowY === "auto" || overflowY === "scroll") &&
          node.scrollHeight > node.clientHeight
        ) {
          scrollTargets.add(node);
        }
        node = node.parentElement;
      }
    };

    const pickByViewportLine = () => {
      const vh = window.innerHeight;
      const bandTop = vh * 0.22;
      const bandBottom = vh * 0.52;
      const focusLine = vh * 0.36;
      let bestId = null;
      let bestOverlap = 0;
      let nearestVisibleId = null;
      let nearestVisibleDist = Infinity;

      for (const q of questionList) {
        const el = questionRefs.current[q.id];
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        const visible = rect.bottom > 0 && rect.top < vh;
        if (!visible) continue;

        const overlapTop = Math.max(rect.top, bandTop);
        const overlapBottom = Math.min(rect.bottom, bandBottom);
        const overlap = Math.max(0, overlapBottom - overlapTop);
        if (overlap > bestOverlap) {
          bestOverlap = overlap;
          bestId = String(q.id);
        }

        const center = rect.top + rect.height / 2;
        const dist = Math.abs(center - focusLine);
        if (dist < nearestVisibleDist) {
          nearestVisibleDist = dist;
          nearestVisibleId = String(q.id);
        }
      }

      const chosen = bestOverlap > 0 ? bestId : nearestVisibleId;
      if (chosen && chosen !== lastFocusedRef.current) {
        lastFocusedRef.current = chosen;
        onFocusedQuestionChange(chosen);
      }
    };

    if (focusMode === "viewport-line") {
      let raf = 0;
      const schedule = () => {
        if (raf) return;
        raf = window.requestAnimationFrame(() => {
          raf = 0;
          pickByViewportLine();
        });
      };
      const attach = () => {
        bindScrollTargets();
        scrollTargets.forEach((target) => {
          target.addEventListener("scroll", schedule, { passive: true });
        });
      };
      const detach = () => {
        scrollTargets.forEach((target) => {
          target.removeEventListener("scroll", schedule);
        });
      };
      attach();
      schedule();
      window.requestAnimationFrame(schedule);
      window.addEventListener("resize", schedule);
      return () => {
        detach();
        window.removeEventListener("resize", schedule);
        if (raf) window.cancelAnimationFrame(raf);
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (!visible.length) return;
        const qid = visible[0].target.getAttribute("data-question-id");
        if (qid) onFocusedQuestionChange(qid);
      },
      { root: null, rootMargin: "-15% 0px -50% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    questionList.forEach((q) => {
      const el = questionRefs.current[q.id];
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [questionList, onFocusedQuestionChange, focusMode]);

  if (!questionList.length) return null;

  return (
    <div ref={listRootRef} className={sectionLabel ? "mt-4" : "mt-2"}>
      {sectionLabel && (
        <p className="text-sm font-semibold text-[#004F73] mb-3">{sectionLabel}</p>
      )}
      {questionList.map((question) => {
        const qtype = resolveQuestionType(question);
        const isGuideFocused =
          focusMode === "viewport-line" &&
          activeQuestionId != null &&
          String(question.id) === String(activeQuestionId);
        return (
          <div
            key={question.id}
            ref={(el) => {
              questionRefs.current[question.id] = el;
            }}
            data-question-id={question.id}
            className={`my-6 rounded-xl transition-shadow duration-200 ${
              isGuideFocused
                ? "ring-2 ring-[#004F73]/25 shadow-sm bg-white/80 -mx-2 px-2 py-1"
                : ""
            }`}
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-5">
              <div className="flex items-start gap-4 flex-1 min-w-0">
                <div className="shrink-0">
                  <span className="w-8 h-6 md:h-10 md:w-10 flex justify-center items-center bg-[#004F73] rounded-full text-white text-sm md:text-sm">
                    {question.display_number || question.id}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  {/* question text with left-border accent */}
                  <div
                    style={{
                      borderLeft: "3px solid rgba(0,79,115,0.30)",
                      paddingLeft: 14,
                    }}
                  >
                    <h3 className="text-lg md:text-lg font-bold text-gray-900">
                      <QuestionText
                        text={question.text}
                        terms={question.terms}
                        onAskTerm={onAskTerm}
                      />
                    </h3>
                    {onAskQuestion && (
                      <button
                        type="button"
                        onClick={() => onAskQuestion(question)}
                        className="sfa-ask-ai-btn mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#004F73] rounded-full border border-[#004F73]/25 bg-[#004F73]/5 px-2.5 py-1 hover:bg-[#004F73]/10 whitespace-nowrap"
                      >
                        Ask AI about this
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {(onEditQuestion || onDeleteQuestion || onMoveQuestion) && (
                <div className="flex items-center gap-1 shrink-0">
                  {onMoveQuestion && (
                    <>
                      <button
                        type="button"
                        onClick={() => onMoveQuestion(question, "up")}
                        className="p-2 border rounded-full bg-white cursor-pointer text-gray-500 hover:bg-gray-50 hover:text-[#004F73]"
                        title="Move up"
                        aria-label="Move question up"
                      >
                        <ChevronUpIcon />
                      </button>
                      <button
                        type="button"
                        onClick={() => onMoveQuestion(question, "down")}
                        className="p-2 border rounded-full bg-white cursor-pointer text-gray-500 hover:bg-gray-50 hover:text-[#004F73]"
                        title="Move down"
                        aria-label="Move question down"
                      >
                        <ChevronDownIcon />
                      </button>
                    </>
                  )}
                  {onEditQuestion && (
                    <button
                      type="button"
                      onClick={() => onEditQuestion(question)}
                      className="p-3 border rounded-full bg-white cursor-pointer text-gray-700 hover:bg-gray-50"
                      title="Edit question"
                      aria-label="Edit question"
                    >
                      <EditIcon />
                    </button>
                  )}
                  {onDeleteQuestion && (
                    <button
                      type="button"
                      onClick={() => onDeleteQuestion(question)}
                      className="p-3 border rounded-full bg-white cursor-pointer text-red-400 hover:bg-red-50 hover:text-red-600"
                      title="Delete question"
                      aria-label="Delete question"
                    >
                      <TrashIcon />
                    </button>
                  )}
                </div>
              )}
            </div>

            {qtype === "text" ? (
              <div className="bg-white p-4 my-2 rounded-2xl border border-gray-100 shadow-sm">
                {renderTextQuestionBlock(
                  question,
                  answers,
                  setAnswerField,
                  readOnly
                )}
              </div>
            ) : qtype === "dropdown" ? (
              <div className="bg-white p-4 my-2 rounded-2xl border border-gray-100 shadow-sm">
                {renderDropdownQuestionBlock(
                  question,
                  answers,
                  setAnswerField,
                  readOnly
                )}
              </div>
            ) : qtype === "multi" ? (
              <div className="bg-white p-4 my-2 rounded-2xl border border-gray-100 shadow-sm">
                {renderMultiQuestionBlock(
                  question,
                  answers,
                  setAnswerField,
                  readOnly
                )}
              </div>
            ) : (
              (question.parts || []).map((part, index) => {
                const inner = renderPartBlock(
                  question,
                  qtype,
                  part,
                  answers,
                  setAnswerField,
                  readOnly,
                  assessmentType
                );
                if (!inner) return null;
                return (
                  <div
                    key={`${question.id}-${index}`}
                    className="bg-white p-4 my-2 rounded-2xl border border-gray-100 shadow-sm"
                  >
                    {inner}
                  </div>
                );
              })
            )}
          </div>
        );
      })}
    </div>
  );
}

const PRODUCT_LOGO_SRC = "/5x-logo-tagline.png";
const DEFAULT_COMPANY_LOGO_SRC = "/company-logo-placeholder.png";

function AssessmentTopNav({
  companyName,
  companyLogo,
  headerRight,
  topNavVariant = "participant",
  productLogoUrl = null,
  productDisplayName = null,
}) {
  const logoSrc = companyLogo || DEFAULT_COMPANY_LOGO_SRC;
  const displayName = companyName || "Company Name";
  const isAdmin = topNavVariant === "admin";
  const productSrc = productLogoUrl || (productDisplayName ? null : PRODUCT_LOGO_SRC);
  const showProductText = Boolean(productDisplayName) && !productLogoUrl;

  if (isAdmin) {
    return (
      <nav className="sticky top-0 z-20 shrink-0 px-5 py-2 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="shrink-0 min-w-0">
            <img
              src={logoSrc}
              alt=""
              className="w-10 h-10 object-contain border-4 border-white shrink-0 bg-white"
            />
            <p className="font-bold text-gray-900 truncate max-w-[8rem] sm:max-w-xs">
              {displayName}
            </p>
          </div>
          <div className="flex items-center gap-3 sm:gap-4 shrink-0">
            {headerRight}
            <div className="rounded-lg relative bg-white shrink-0">
              {showProductText || !productSrc ? (
                <p className="font-bold text-[#004f73] text-lg px-2 py-2 max-w-[14rem] truncate">
                  {productDisplayName || "Assessment"}
                </p>
              ) : (
                <img
                  src={productSrc}
                  alt={productDisplayName || "Logo"}
                  className="w-56 h-[3.5rem] object-contain"
                />
              )}
            </div>
          </div>
        </div>
      </nav>
    );
  }

  /* v1 SurveyAdmin participant assessment nav */
  return (
    <nav className="px-5 py-2 bg-white shrink-0">
      <div className="mx-auto flex items-center justify-between">
        <div className="rounded-lg relative bg-white">
          {showProductText || !productSrc ? (
            <p className="font-bold text-[#004f73] text-lg px-2 py-2 max-w-[14rem] truncate">
              {productDisplayName || "Assessment"}
            </p>
          ) : (
            <img
              src={productSrc}
              alt={productDisplayName || "Logo"}
              className="w-56 h-[3.5rem] object-contain"
            />
          )}
        </div>
        <div className="flex items-center">
          {headerRight}
          <img
            src={logoSrc}
            alt="Profile"
            className="w-10 h-10 border-4 border-white object-contain bg-white"
          />
          <p className="font-bold">{displayName}</p>
        </div>
      </div>
    </nav>
  );
}

function EditIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="w-5 h-5"
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"
      />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="w-5 h-5"
      aria-hidden
    >
      <polyline strokeLinecap="round" strokeLinejoin="round" points="3 6 5 6 21 6" />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 11v6M14 11v6" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
    </svg>
  );
}

function ChevronUpIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="w-4 h-4"
      aria-hidden
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M18 15l-6-6-6 6" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="w-4 h-4"
      aria-hidden
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
    </svg>
  );
}

export default function V1StyleAssessmentLayout({
  companyName,
  companyLogo,
  trackBanner,
  headerRight,
  topNavVariant = "participant",
  hideTopNav = false,
  singlePageMode = false,
  steps,
  currentStep,
  children,
  footer,
  assessmentType,
  productLogoUrl = null,
  productDisplayName = null,
}) {
  const currentSurvey = steps[currentStep - 1];
  const importanceLabel = scaleLabel(assessmentType, "importance");
  const agreementLabel = scaleLabel(assessmentType, "agreement");
  const importanceOpts = scaleOptions(assessmentType, "importance");
  const agreementOpts = scaleOptions(assessmentType, "agreement").filter(
    (o) => String(o).trim().toLowerCase() !== "unable to assess"
  );
  const liteBanner = trackBanner || trackLabel(assessmentType, "lite");

  return (
    <div
      className={`w-full flex flex-col bg-[#F9F9F9] ${
        hideTopNav ? "" : "min-h-screen"
      }`}
    >
      {!hideTopNav && (
        <AssessmentTopNav
          companyName={companyName}
          companyLogo={companyLogo}
          headerRight={headerRight}
          topNavVariant={topNavVariant}
          productLogoUrl={productLogoUrl}
          productDisplayName={productDisplayName}
        />
      )}

      <div className="flex-1 p-4 md:p-5">
        <div className="max-w-7xl mx-auto">
          <div className="md:px-6 md:py-6 p-4 bg-white rounded-2xl border border-gray-100 shadow-sm">
            {singlePageMode ? (
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-xl font-bold text-[#004F73] py-1">
                    {liteBanner}
                  </p>
                  <div className="text-sm text-gray-700 mt-3 leading-relaxed space-y-3">
                    <p>
                      <strong>{importanceLabel}</strong> – How critical is this
                      statement to the company&apos;s success?
                      <br />
                      <span className="text-gray-600">
                        Response options: {formatOptionList([...importanceOpts].reverse())}
                      </span>
                    </p>
                    <p>
                      <strong>{agreementLabel}</strong> – How well do you
                      believe the organization is currently performing or executing
                      relative to the statement?
                      <br />
                      <span className="text-gray-600">
                        Response options: {formatOptionList(agreementOpts)}
                      </span>
                    </p>
                    <p>
                      If you do not have sufficient knowledge or observable data
                      to make an assessment, please select &quot;Unable to
                      Assess.&quot;
                    </p>
                  </div>
                </div>
                {headerRight}
              </div>
            ) : hideTopNav && (headerRight || trackBanner) ? (
              <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                <div className="min-w-0 flex-1">
                  <p className="text-xl font-bold text-[#004F73] py-1">
                    Assessment Section {currentStep}:{" "}
                    {currentSurvey?.title || "Assessment"}
                  </p>
                  {trackBanner && (
                    <span className="text-xs font-semibold text-[#004F73]">
                      {trackBanner}
                    </span>
                  )}
                </div>
                {headerRight}
              </div>
            ) : (
              <>
                <p className="text-xl font-bold text-[#004F73] py-1">
                  Assessment Section {currentStep}:{" "}
                  {currentSurvey?.title || "Assessment"}
                </p>
                {trackBanner && (
                  <p className="text-xs font-semibold text-[#004F73] mb-1">
                    {trackBanner}
                  </p>
                )}
              </>
            )}
            {!singlePageMode && (
              <>
                <p className="text-sm text-gray-700">
                  {currentSurvey?.subhead || "Assessment"}
                </p>
                <StepIntro step={currentSurvey} />
              </>
            )}
          </div>

          {!singlePageMode && (
            <div className="py-4 md:py-8 flex items-center w-full relative justify-between px-1">
              <div className="absolute top-1/2 left-0 right-0 h-[2px] bg-[#e5e5e5] transform -translate-y-1/2" />
              <button
                type="button"
                className="relative z-10 w-8 h-6 md:h-10 md:w-16 flex justify-center items-center bg-[#004F73] rounded-full text-white text-xs md:text-sm cursor-default"
                tabIndex={-1}
              >
                Start
              </button>
              {steps.map((stepData, index) => (
                <span
                  key={index}
                  className={`relative z-10 w-8 h-6 md:h-10 md:w-10 flex justify-center items-center ${
                    stepData.is_completed === "Y"
                      ? "bg-[#004F73] text-white"
                      : currentStep === index + 1
                        ? "bg-[#004F73] text-white"
                        : "bg-[#f5f5f5] text-[#004F73]"
                  } border border-[#004F73] rounded-full text-xs md:text-sm`}
                >
                  {stepData.is_completed === "Y" ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      className="w-4 h-4"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </span>
              ))}
              <button
                type="button"
                className="relative z-10 w-8 h-6 md:h-10 md:w-16 flex justify-center items-center bg-[#004F73] rounded-full text-white text-xs md:text-sm cursor-default"
                tabIndex={-1}
              >
                Submit
              </button>
            </div>
          )}

          {children}

          {footer && (
            <div className="flex flex-col gap-4 mt-8 w-full">{footer}</div>
          )}
        </div>
      </div>
    </div>
  );
}
